import { jsx as _jsx } from "react/jsx-runtime";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { useRealCommerceErrorHandling } from "../../../../hooks/useRealCommerceErrorHandling.js";
import { getPaddleCheckoutAttributes } from "../../../../others/getPaddleCheckoutAttributes.js";
import { useStores } from "../../../../store/stores.js";
import { __ } from "../../../../utils/i18n.js";
import { CheckoutStep } from "../../store/CheckoutV2Store.js";
/**
 * Inline Paddle checkout iframe. Ported 1:1 from v1's `paddle.tsx` with only
 * store/import paths adapted to checkout V2 — the Paddle-side interaction
 * (event handlers in `PaddleTransaction`, passthrough payload, price-sync
 * error recovery) must stay identical so orders keep reconciling with the
 * backend's incomplete-checkout record.
 *
 * @see https://developer.paddle.com/guides/how-tos/checkout/inline-checkout
 */ const PaddleFrame = observer(({ style })=>{
    const [isOpen, setOpen] = useState(false);
    const ref = useRef(null);
    const { checkoutV2Store } = useStores();
    const { transaction, product: { data: { productPlan: { ecommerceProductPlanId } } } } = checkoutV2Store;
    const { identification, incompleteCheckout, lastSyncCouponWithIncompleteCheckoutError } = transaction;
    const checkoutUrl = incompleteCheckout?.checkoutUrl;
    const paddleTransaction = transaction;
    const { Paddle, hasAddedVat } = paddleTransaction;
    const handleRealCommerceError = useRealCommerceErrorHandling();
    const frameTarget = `paddle-checkout-frame-${ecommerceProductPlanId}`;
    useEffect(()=>{
        if (ref.current && Paddle && identification && !isOpen) {
            setOpen(true);
            const { email, zip, country, coupon } = identification;
            const emailNormalized = String(email).toLowerCase();
            const passthrough = {
                realCommerceCheckoutUuid: incompleteCheckout?.checkout.uuid
            };
            Paddle.Checkout.open({
                ...getPaddleCheckoutAttributes(),
                // Override (https://developer.paddle.com/api-reference/product-api/pay-links/createpaylink)
                override: checkoutUrl,
                passthrough: JSON.stringify(passthrough),
                // Iframe configuration
                frameTarget,
                loadCallback: function() {
                    checkoutV2Store.setStep(CheckoutStep.PaymentReady);
                },
                // Data of transaction
                product: ecommerceProductPlanId,
                email: emailNormalized,
                postcode: zip,
                country,
                coupon
            });
        }
    }, [
        ref,
        Paddle,
        frameTarget,
        identification,
        checkoutUrl,
        isOpen
    ]);
    useEffect(()=>{
        if (lastSyncCouponWithIncompleteCheckoutError) {
            const onBackendDown = (error)=>checkoutV2Store.setFatalError(error);
            if (hasAddedVat) {
                handleRealCommerceError(lastSyncCouponWithIncompleteCheckoutError, {
                    alwaysModal: true,
                    subTitle: __("You have added a company address to your order. The order process will now be restarted. Please add your company address again."),
                    onBackendDown
                });
            } else {
                handleRealCommerceError(lastSyncCouponWithIncompleteCheckoutError, {
                    onBackendDown
                });
            }
            // Listen to coupon changes which ended up in an error (e.g. a coupon can only be used only once) and delete the coupon
            // from the checkout.
            if (lastSyncCouponWithIncompleteCheckoutError.hasErrorCode([
                "CheckoutCouponNotExistYet",
                "CheckoutCouponOnlyForNewCustomers",
                "CheckoutCoupontRestrictionTriggeredMaxUsages"
            ])) {
                // Reset step to indicate loading symbol
                checkoutV2Store.setStep(CheckoutStep.IdentificationSubmitted);
                Paddle.Checkout.open({
                    ...window._activeCheckout ?? {},
                    coupon: null
                });
            }
        }
    }, [
        lastSyncCouponWithIncompleteCheckoutError,
        hasAddedVat
    ]);
    return /*#__PURE__*/ _jsx("div", {
        className: frameTarget,
        ref: ref,
        style: style
    });
});
export { PaddleFrame };
